import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useCurrentIdentity } from '@/lib/identity-context';
import { queryKeys } from '@/lib/query-keys';
import { Card, CardContent } from '@/components/ui/card';
import { CoverageReport } from './harness-coverage-report';
import { extractErrorMessage } from './harness-error';

export function HarnessCoveragePage({ crawlId }: { crawlId: string }) {
  const { hasPermission } = useCurrentIdentity();
  const canRun = hasPermission('harness:run');

  const coverageQuery = useQuery({
    queryKey: queryKeys.harness.coverage(crawlId),
    enabled: canRun,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      // Keep polling while the scan is still in flight.
      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        return false;
      }
      return 5000;
    },
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/harness-scans/{crawlId}/coverage',
        { params: { path: { crawlId } } }
      );
      if (error) throw error;
      return data;
    },
  });

  if (!canRun) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          You don&rsquo;t have permission to view harness coverage.
        </CardContent>
      </Card>
    );
  }

  if (coverageQuery.isLoading) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  if (coverageQuery.isError || !coverageQuery.data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">
          {extractErrorMessage(coverageQuery.error)}
        </CardContent>
      </Card>
    );
  }

  return <CoverageReport report={coverageQuery.data} />;
}
